/**
 * SkeletonTable - Prebuilt skeleton for table layouts with Framer Motion animations
 *
 * Provides a ready-to-use skeleton loader for table-based content with stagger animations.
 *
 * @example
 * <SkeletonTable rows={5} columns={4} />
 *
 * @example
 * <SkeletonTable rows={10} columns={6} showActions animation="wave" />
 */

import React from 'react';
import PropTypes from 'prop-types';
import Skeleton from './Skeleton';
import { cn } from '../utils/classNames';

const SkeletonTable = ({
  rows = 5,
  columns = 4,
  showActions = false,
  className,
  animation = 'shimmer',
  duration = 1.5,
}) => {
  const totalColumns = showActions ? columns + 1 : columns;

  return (
    <div className={cn('bg-white rounded-lg shadow overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {/* Table Header */}
          <thead className="bg-gray-50">
            <tr className="animate-fade-in">
              {Array.from({ length: totalColumns }, (_, index) => (
                <th key={index} className="px-6 py-3">
                  <Skeleton
                    variant="text"
                    width="80%"
                    height="16px"
                    animation={animation}
                    duration={duration}
                  />
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.from({ length: rows }, (_, rowIndex) => (
              <tr
                key={rowIndex}
                className="animate-fade-in"
                style={{ animationDelay: `${rowIndex * 0.05}s` }}
              >
                {Array.from({ length: totalColumns }, (_, colIndex) => (
                  <td key={colIndex} className="px-6 py-4 whitespace-nowrap">
                    {showActions && colIndex === totalColumns - 1 ? (
                      <div className="flex gap-2">
                        <Skeleton
                          variant="circle"
                          width="32px"
                          height="32px"
                          animation={animation}
                          duration={duration}
                        />
                        <Skeleton
                          variant="circle"
                          width="32px"
                          height="32px"
                          animation={animation}
                          duration={duration}
                        />
                      </div>
                    ) : (
                      <Skeleton
                        variant="text"
                        width="90%"
                        animation={animation}
                        duration={duration}
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

SkeletonTable.propTypes = {
  /** Number of rows */
  rows: PropTypes.number,
  /** Number of columns */
  columns: PropTypes.number,
  /** Show action buttons column */
  showActions: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Animation type (pulse, shimmer, wave, fade) */
  animation: PropTypes.oneOf(['pulse', 'shimmer', 'wave', 'fade']),
  /** Animation duration in seconds */
  duration: PropTypes.number,
};

export default SkeletonTable;
